/** Bottom sheet listing the header pills that didn't fit the mobile row.
 *
 *  A plain action list (icon + label per row) rather than the DropdownMenu the
 *  trailing "More" pills use: at phone width a sheet gives every row a real
 *  touch target, scrolls when the list is long, and can't be clipped by the
 *  row it came from. Rows are already flattened by flattenOverflowActions —
 *  this component never sees a nested menu. */

import { Check } from '@/shared/ui/icons';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../shared/ui/sheet';
import type { MobileHeaderSubAction } from './types';

export function HeaderOverflowSheet({
  open,
  onOpenChange,
  title,
  items,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  items: MobileHeaderSubAction[];
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton
        className="p-0 gap-0 flex flex-col max-h-[75vh] rounded-t-xl pb-[env(safe-area-inset-bottom)]"
      >
        <SheetHeader className="px-4 py-3 border-b border-border flex-shrink-0">
          <SheetTitle className="text-sm">{title}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-1" role="menu">
          {items.map((item) => {
            const Icon = item.icon;
            const TrailingIcon = item.trailingIcon;
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                disabled={!!item.disabled}
                aria-label={item.ariaLabel ?? item.label}
                // Dismiss before running the action: several of these dispatch
                // window events that open other surfaces, and a sheet still
                // closing would fight them for focus.
                onClick={() => {
                  onOpenChange(false);
                  item.onClick();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-accent disabled:opacity-40"
              >
                {Icon && (
                  <Icon
                    className="flex-shrink-0"
                    style={{ width: 18, height: 18, color: 'var(--foreground)' }}
                    strokeWidth={1.8}
                  />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className="font-apple truncate leading-tight"
                    style={{ fontSize: 15, color: 'var(--foreground)' }}
                  >
                    {item.label}
                  </span>
                  {item.sublabel && (
                    <span
                      className="font-apple truncate leading-tight"
                      style={{ fontSize: 11, color: 'var(--muted-foreground)' }}
                    >
                      {item.sublabel}
                    </span>
                  )}
                </span>
                {/* Builders mark the selected row either with an explicit
                    trailingIcon or with `active` alone — the sheet ticks both. */}
                {TrailingIcon ? (
                  <TrailingIcon
                    className="flex-shrink-0"
                    style={{ width: 16, height: 16, color: 'var(--primary)' }}
                    strokeWidth={2}
                  />
                ) : item.active ? (
                  <Check
                    className="flex-shrink-0"
                    style={{ width: 16, height: 16, color: 'var(--primary)' }}
                    strokeWidth={2}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
